import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DataTable } from '@/components/common/DataTable';
import { RBAC_PERMISSIONS } from '@/constants/permissions';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { rolesQueryOptions } from '../api/roles.api';
import { useRoleActionModals } from '../hooks/useRoleActionModals';
import type { Role } from '../types/role.types';
import { getRoleColumns } from './RoleColumns';
import { RoleDeleteConfirmation } from './RoleDeleteConfirmation';
import { RoleFormModal } from './RoleFormModal';

const route = getRouteApi('/_auth/users/roles');

const toSearchableValues = (role: Role) => [role.name, role.description, role.code];

export function RoleListTable() {
  const { q: appliedSearchText } = route.useSearch();
  const navigate = route.useNavigate();

  const {
    modalState,
    roleToDelete,
    openCreateModal,
    openEditModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
  } = useRoleActionModals();

  const { data, isLoading, error, refetch } = useQuery(rolesQueryOptions());

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: data,
    appliedText: appliedSearchText,
    onApply: (text) => void navigate({ search: { q: text }, replace: true }),
    toSearchableValues,
  });

  const columns = useMemo(
    () =>
      getRoleColumns(appliedSearchText, {
        onEdit: openEditModal,
        onDelete: openDeleteModal,
      }),
    [appliedSearchText, openEditModal, openDeleteModal],
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Roles"
          search={{
            value: searchText,
            onChange: setSearchText,
            placeholder: 'Search by name or details',
          }}
          createAction={{
            label: 'Add Role',
            onClick: openCreateModal,
            requires: RBAC_PERMISSIONS.ROLE_CREATE,
          }}
        />

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load roles"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <DataTable
            data={visibleRows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="name"
            className="lg:flex-1"
            emptyMessage="No roles found"
            emptyHint="Add a role, or change the search to see more."
          />
        )}
      </div>

      <RoleFormModal
        role={modalState?.role ?? null}
        open={modalState !== null}
        onOpenChange={(open) => !open && closeModal()}
      />

      <RoleDeleteConfirmation
        role={roleToDelete}
        open={roleToDelete !== null}
        onOpenChange={(open) => !open && closeDeleteModal()}
      />
    </>
  );
}
